import { GuideView } from './ui/guide';
import { commandStep } from './ui/tips';
// Footmen Wars client (2026-10-25, checkpoint 1): the sim drawn in three.js with one instanced mesh per class, a HUD
// in the DOM, and pointer controls (drag to pan, wheel or pinch to zoom, tap to send your army).
// URL: ?seed=N  ?bots=4 (watch four bots, no human)  ?speed=N  ?army=<faction> (yours, else you pick at tier 1)
//      ?qa=1 (numbers on window.__qa)  ?capture=1 (no clock: window.__advance(ticks) steps and draws, for video)
//      ?armies=<faction> (every bot plays it; a list, ?armies=knights,greeks,vikings,samurai, gives seat p the p-th)  ?premium=0 (placeholder soldiers for every army)  ?zoom=0.5 (closer watching camera)
//      ?hero=<musashi|tomoe|joan|...> (yours, else you pick as the match starts; a capture takes the first)
//      ?room=CODE (opens the lobby and joins that online room: an invite link)
//      ?season=<spring|summer|autumn|winter> ?weather=<clear|overcast|rain|snow> ?tod=<dawn|morning|midday|afternoon|
//      golden|dusk|night> or ?hour=19.5, ?clock=<minutes> (the sky as at that match minute): sky.ts skyPin
// With none of those the page opens on the title (ui/screens.ts) over an attract match; ?title=1 forces it (QA shots).
import * as THREE from 'three';
import { C, CLASSES, ClassId, classOf, DT, FACTION, FACTIONS, FactionId, HERO, HEROES, HERO_RULES, HeroId, TIER_COST, TIER_GATE, BASE } from '../sim/defs';
import { CAP, NEUTRAL, PLAYERS, SIZE, STATE, Sim, type Command } from '../sim/sim';
import { Bot } from '../sim/bot';
import { unitGeometry, unitMaterial } from './units';
import { ARMY_ART, HERO_ARMY, Retry, armyFor, loadArmy, type ArmyId, type PremiumClass } from './premium';
import { FIGURES, loadFigure, type FigureId, type PremiumFigure } from './figures';
import { uploadFirst } from './baked';
import { corpseAt, gaitRow } from './mount';
import { Turns, turnSpec } from './turn';
import { DISC_R, DISC_SCALE, DISC_TOP, FADES, SUN_DIR, Stage, blobShadows, gpuName, hdrTargets, isMobile, pickTier, puffGeometry, teamDiscs } from './look';
import { buildBase, buildCenter, type BaseView } from './buildings';
import { buildWorld } from './board';
import { Sky, paint, paintSeason, skyPin, summerOf, type SkyState } from './sky';
import { PALETTE } from './palette';
import { groundColors, type Ground } from './ui/minimap';
import { Precip } from './precip';
import { bridgesFor, liftAt, liftUnder } from './river';
import { Hud } from './hud';
import { Controls } from './control/controls';
import { OrderMarker } from './control/marker';
import { Screens, type Mode } from './ui/screens';
import { clockRuns, startScreen } from './ui/flow';
import { PlayStats, beacon } from './stats';
import { isTestVisit, statsAllowed, viaOf } from '../stats/schema';
import { ARMY_KEY, CAMERA_ZOOM, DEFAULTS, loadSettings, manualQuality, saveSettings, type Settings } from './ui/settings';
import { GameAudio } from './audio';
import { ambienceMix, choosingArmy, distanceGain, musicPhase } from './audio-logic';
import { eventCues, swingKind, type Cue } from './audio-events';
import { autoQuality, qualityWindow } from './autoquality';
import { CameraRig, ease } from './camera';
import { TEAM_NAME, mmss } from './ui/data';
import { Online } from './online';
import { humanTakes, startBots, takeOver } from '../net/lockstep';
import { JUMP_KEYS, parseJump } from './jumpin';
import { armiesOf, matchBots, pickSeat, seatInView } from './jumpmatch';
import { parseAim, type Aim } from '../site/livehero';
import { HASH_EVERY, type Seat, type Start } from '../net/protocol';
import { countsLocal } from '../net/played';

let qualityPerf = qualityWindow();
let qualityLoads = 0;
// Automatic Light is session-only; saved graphics remain the player's preference. It lightens the stage (shadows,
// post, pixel ratio) and keeps the 3D armies: a step-down that swapped them for placeholders read as missing models.
let autoLight = false;
const params = new URLSearchParams(location.search);
// a jump address that fails parseJump (a number out of range, a cut link) opens as plain /play/: left in, its seed and
// armies would skip the title into a match of its own (Flash F7, 2026-11-01)
if (params.has('jump') && !parseJump(params)) {
  for (const k of JUMP_KEYS) params.delete(k);
  const rest = params.toString();
  history.replaceState(history.state, '', location.pathname + (rest ? `?${rest}` : '') + location.hash);
}
const seed = Number(params.get('seed') ?? 1);
const capture = params.get('capture') === '1';
// the live hero (?live=1, below) is the site's picture, not this visitor's game: never their saved Light graphics or
// blur off (the frame shares the game's storage; Flash round 4 F2). Nothing in it saves settings.
const settings = params.get('live') === '1' ? { ...DEFAULTS } : loadSettings();
const audio = new GameAudio();
audio.apply(settings);
let lastArmy: FactionId | null = null;
try { const a = localStorage.getItem(ARMY_KEY); if (FACTIONS.includes(a as FactionId)) lastArmy = a as FactionId; } catch { /* private mode */ }
/** Saved when the sim accepts your own pick (hooks.pick), at once: never for an ?army= or ?autoplay seat (a URL param or
 *  a bot is never saved), and not lost to a quit in the frame before the tier event (2026-10-31, Flash F3). */
function saveArmy(f: FactionId) {
  lastArmy = f;
  try { localStorage.setItem(ARMY_KEY, f); } catch { /* session choice holds */ }
}
const opening = startScreen(params);
/** ?live=1 (2026-11-01, the landing's live hero, src/site/livehero.ts): this page in a frame behind the landing's
 *  headline, a bot match from ?seed= with no HUD, title, sound or stats and a calm camera, run from its quiet first
 *  minutes; window.__live is what the landing reads (Jump in) and drives (pause). */
const live = params.get('live') === '1';
document.documentElement.toggleAttribute('data-live', live);
/** The point of the frame the live hero's fight goes to (?aim=u,v, livehero.ts aimOf: beside or under the landing's
 *  logo, clear of its copy), or null for the middle. The landing sends it again on a resize (__live.aim). */
let liveAim: Aim | null = live ? parseAim(params.get('aim')) : null;
let speed = live ? 1 : Number(params.get('speed') ?? settings.speed);
/** ?jump=<seat>&seed&tick (jumpin.ts): Jump in from the landing. The hero's match is rebuilt, run to the tick behind a
 *  cover, and you take that corner from its bot (takeJump). Null until then and after. */
let jump = parseJump(params);
// index.html paints a jump's first frame navy (the cover's colour); an address that is no jump after all gets the sky back
if (!jump && document.documentElement.dataset.jump === 'catching-up') delete document.documentElement.dataset.jump;
/** Your seat, or -1 when watching. The title's attract match and Watch a match are -1; Play vs bots is 0; a jump is -1
 *  (every corner a bot, as the hero made it) until you take yours. */
let ME = opening === 'title' || params.get('bots') === '4' || live || jump ? -1 : 0;
/** ?autoplay=1: a bot plays your seat while the HUD shows it (for checkpoint videos). */
const autoplay = params.get('autoplay') === '1';
/** Stats (src/stats/schema.ts, 2026-10-31): only from the playtest host and footmenwars.com (2026-11-01), never with Do Not Track or Global Privacy
 *  Control on, never a capture. Any address setting (?qa=1, ?hero=, ?speed=, ...; the tags a real visitor brings aside, isTestVisit) marks the
 *  visit a test, which scripts/stats.ts leaves out (Flash F7). */
// (the landing's live hero is no visit, and a Jump in's own keys are not a test's)
const stats = !capture && !live && statsAllowed(location, navigator as Navigator & { globalPrivacyControl?: boolean }, window as { doNotTrack?: string })
  ? new PlayStats(beacon, isTestVisit([...params.keys()].filter((k) => !jump || !(JUMP_KEYS as readonly string[]).includes(k))), matchMedia('(pointer: coarse)').matches ? 'touch' : 'mouse')
  : null;
addEventListener('error', (e) => stats?.error(e.message, e.filename, e.lineno));
addEventListener('unhandledrejection', (e) => stats?.error(e.reason instanceof Error ? e.reason.message : e.reason));

export const TEAM_COLORS = [0xd8433a, 0x3a72d8, 0xd6a631, 0x2fa89a, 0x8f8f99]; // red, blue, bronze-gold, teal; neutral grey
export const ARMY_BODY: Record<FactionId | 'none', number> = {
  none: 0xb8bcc4, knights: 0xc9ced6, samurai: 0x3d3a40, greeks: 0xece4cf, vikings: 0x8c6c4c,
  mongols: 0xa57d52, chinese: 0x7a4a3a, aztecs: 0xdccc9e, egyptians: 0xeee3c2,
};

let sim = new Sim(seed);
/** Each soldier's drawn facing, eased toward the sim's (turn.ts, 2026-10-30); ?turn=0 draws the sim's facing as is. */
const turns = new Turns(CAP), smoothTurns = params.get('turn') !== '0';
/** The facing unit i is drawn at, at the last frame's alpha (the sim's until its first tick). */
const drawnFace = (i: number) => (smoothTurns && turns.uid[i] === sim.uid[i] ? turns.at(i, drawAlpha) : sim.face[i]);
let bots: Bot[] = [];
let pickOffered = false, heroOffered = false;
/** The match's season, weather and time of day (sky.ts): a pure function of the seed and the match clock. */
let skyPinned = skyPin(params);
let sky = new Sky(seed, skyPinned);
/** `seats`: an online match (2026-11-01). Its bots are the room's (one for each seat no person holds, made in seat order
 *  as on every other device), and the room seats every person's hero in turn 0, so no hero pick and no address knobs. */
/** The seed the match on the table was made from (the live hero's Jump in hands it on). */
let matchSeed = seed;
function newMatch(s: number, seats?: readonly Seat[]) {
  qualityPerf = qualityWindow();
  sim = new Sim(s); turns.reset(); matchSeed = s;
  sky = new Sky(s, skyPinned); paintSeason(sky.season);
  pickOffered = false; heroOffered = false; myArmy = null;
  if (seats) { bots = startBots(sim, seats); heroOffered = true; return; }
  // one builder with the live hero and Jump in (jumpmatch.ts), so a match rebuilt from a seed is the same match
  bots = matchBots(sim, armiesOf((params.get('armies') ?? '').split(',')), autoplay ? -1 : ME);
  if (ME >= 0 && params.get('army')) myArmy = params.get('army') as FactionId;
  const hero = params.get('hero') as HeroId | null;
  if (ME >= 0 && !autoplay && (hero && HEROES.includes(hero) || capture)) sim.command(ME, { t: 'hero', id: hero && HEROES.includes(hero) ? hero : HEROES[0] });
}
let myArmy: FactionId | null = null;
newMatch(opening === 'title' ? randomSeed() : seed);
function randomSeed() { return 1 + Math.floor(Math.random() * 999_999); }
const prevX = new Float32Array(CAP), prevY = new Float32Array(CAP);
/** How far between the last two steps the last frame drew the soldiers: a tap picks them where they were drawn. */
let drawAlpha = 1;
/** When each soldier last struck (sim seconds), read from its cooldown restarting, so a 3D attack plays from the
 *  sim's real strike and then stands until the next (a free-running clock fired the siege's bolt three times per
 *  two shots, Astra 2026-10-25). */
const struckAt = new Float32Array(CAP).fill(-1e9), prevCd = new Float32Array(CAP);
/** When each soldier came out (sim seconds), for his drop onto his disc (STYLE.md: toy moments at spawn). */
const spawnAt = new Float32Array(CAP).fill(-1e9);

// ---- renderer, camera
const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance', preserveDrawingBuffer: capture });
/** Graphics "best" draws the 3D armies, shadows and post (look.ts tiers) at the screen's pixel ratio (up to 2); "light"
 *  (?premium=0 too) placeholders at 1, no shadow map, no post. */
const premiumOn = () => params.get('premium') !== '0' && settings.quality === 'best';
document.getElementById('game')!.appendChild(renderer.domElement);
const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(40, innerWidth / innerHeight, 1, 1200);
/** The look's tier (look.ts pickTier): Light for Fast or ?premium=0, Ultra on a strong desktop GPU, else Best; ?tier=
 *  overrides. ?dof=0 turns off the tilt-shift. */
const mobile = isMobile(navigator.userAgent, navigator.maxTouchPoints);
const stageTier = () => pickTier(premiumOn(), params.get('tier') ?? (autoLight ? 'light' : settings.autoQuality ? null : 'best'), gpuName(renderer), mobile, hdrTargets(renderer));
const stage = new Stage(renderer, scene, camera, stageTier());
if (capture) stage.envRows = 1e9; // a still is one frame: its light map whole
/** The tilt-shift: on unless the settings or ?dof=0 turn it off. */
const dofOn = () => params.get('dof') !== '0' && settings.blur === 'on';
stage.dof = dofOn();
/** Where the camera looks and how far back it sits; camera.ts moves it every frame (keys, wheel, drag glide). */
const cam = new CameraRig(SIZE / 2, SIZE / 2, 150, SIZE);
const calm = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
cam.reduced = !!calm?.matches; calm?.addEventListener?.('change', () => { cam.reduced = calm.matches; });
/** Camera distance from settings (Close, Normal, Far) times ?zoom (scripts/capture.ts films closer with zoom=0.5). */
const camZoom = () => CAMERA_ZOOM[settings.camera] * (Number(params.get('zoom') ?? 1) || 1);
/** Your camera starts over your barracks; a watcher's over the middle. */
function homeCamera() {
  cam.stop();
  cam.place(ME >= 0 ? sim.bases[ME].x + 14 : SIZE / 2, ME >= 0 ? sim.bases[ME].y + 14 : SIZE / 2, ME >= 0 ? 70 * CAMERA_ZOOM[settings.camera] : 150);
}
homeCamera();
function placeCamera() {
  cam.clamp();
  camera.position.set(cam.x, cam.dist * 0.74, cam.z + cam.dist * 0.68);
  camera.lookAt(cam.x, 0, cam.z);
}
const aimRay = new THREE.Vector3();
/** The live hero's aim as a shift on the ground: from the camera's look point to the ground seen at liveAim. The camera
 *  only slides and never turns, so looking at (fight - shift) puts the fight at the aim. [0, 0] with no aim. */
function aimShift(): [number, number] {
  if (!liveAim) return [0, 0];
  camera.updateMatrixWorld();
  aimRay.set(liveAim[0] * 2 - 1, 1 - liveAim[1] * 2, 0.5).unproject(camera).sub(camera.position);
  const t = -camera.position.y / Math.min(-1e-3, aimRay.y); // the camera looks down, so the ray meets the ground
  return [camera.position.x + aimRay.x * t - cam.x, camera.position.z + aimRay.z * t - cam.z];
}
addEventListener('resize', () => stage.resize());
placeCamera();

// ---- the board (board.ts): felt, sand roads, trees, the river and its bridges (river.ts; the map is the same every match)
const bridges = bridgesFor(sim.road);
const world = buildWorld(scene, sim, bridges, !mobile);
stage.addTiered(world);
/** Rain, snow, falling leaves and petals (precip.ts). */
const precip = new Precip();
scene.add(precip.rain, precip.flakes);
let skyNow: SkyState | undefined;
const focus = new THREE.Vector3(), bufSize = new THREE.Vector2();

// ---- the center tower and the four barracks (buildings.ts)
const center = buildCenter(sim.center.x, sim.center.y);
scene.add(center);
const at = <T extends THREE.Object3D>(o: T, x: number, y: number, z: number) => { o.position.set(x, y, z); return o; };
/** In the sun's shadow pass and taking shadows (a group's meshes). */
const shade = <T extends THREE.Object3D>(o: T) => { o.traverse((m) => { m.castShadow = m.receiveShadow = true; }); return o; };
// ---- the center's giant (2026-10-26, defs.ts BOSS): its own figure (figures.ts), or until that loads (or with
// premium off) the armored soldier with a war drum on its belly, at GIANT_SCALE on the tower's parapet walk (its roof
// lifts away while it stands), and a health bar over its head the way the barracks carry theirs
const towerRoof = center.getObjectByName('roof')!, GIANT_SCALE = 3, GIANT_Y = 8.45, GIANT_H = 8.7; // on the parapet walk (buildings.ts)
const drum = new THREE.Group();
drum.add(new THREE.Mesh(new THREE.CylinderGeometry(1.15, 1.15, 1.3, 24), new THREE.MeshPhongMaterial({ color: 0xa33a2a, shininess: 50, specular: 0x333333 })));
for (const y of [-0.62, 0.62]) {
  drum.add(at(new THREE.Mesh(new THREE.CylinderGeometry(1.22, 1.22, 0.14, 24), new THREE.MeshPhongMaterial({ color: 0xb89a5a, shininess: 70 })), 0, y, 0));
  drum.add(at(new THREE.Mesh(new THREE.CircleGeometry(1.08, 24), new THREE.MeshLambertMaterial({ color: 0xefe3c6, side: THREE.DoubleSide })), 0, y * 1.13, 0).rotateX(Math.PI / 2));
}
drum.visible = false; scene.add(shade(drum));
// The giant's bar is framed like the HUD's (navy edge, gold line, a dark track, red with a lit top) and keeps the size it
// has from the usual camera, so zoomed in it is still a slim bar and not a red slab (uiart, 2026-10-26). Size on screen
// goes with the bar's depth from the camera, not the camera's distance to the ground it looks at (the bar is 18.5 m up):
// GIANT_BAR_DEPTH is that depth from the 70 m camera aimed at the tower. Planes back to front; the fill grows from the left.
const GIANT_BAR_W = 6.2, GIANT_BAR_DEPTH = 57;
const barPlane = (w: number, h: number, color: number, y = 0, fromLeft = false) => {
  const g = new THREE.PlaneGeometry(w, h).translate(fromLeft ? w / 2 : 0, y, 0);
  return new THREE.Mesh(g, new THREE.MeshBasicMaterial({ color, depthTest: false }));
};
const giantBarFill = new THREE.Group();
giantBarFill.add(barPlane(GIANT_BAR_W, 0.3, 0xc8382e, 0, true), barPlane(GIANT_BAR_W, 0.09, 0xe8786c, 0.08, true));
giantBarFill.position.x = -GIANT_BAR_W / 2;
const giantBar = new THREE.Group();
giantBar.add(barPlane(GIANT_BAR_W + 0.32, 0.62, 0x1f2a4a), barPlane(GIANT_BAR_W + 0.16, 0.46, 0xf2c443), barPlane(GIANT_BAR_W, 0.3, 0x2a1d1a), giantBarFill);
let barOrder = 10;
giantBar.traverse((m) => { if (m instanceof THREE.Mesh) m.renderOrder = barOrder++; });
giantBar.visible = false; scene.add(giantBar);
const barAt = new THREE.Vector3();
/** Over the giant's head, facing the camera, scaled with its depth so it keeps its usual size on screen up close. */
function placeGiantBar(gi: number) {
  camera.updateMatrixWorld();
  giantBar.position.set(sim.x[gi], GIANT_Y + GIANT_H + 1.4, sim.y[gi]); giantBar.quaternion.copy(camera.quaternion);
  const depth = -barAt.copy(giantBar.position).applyMatrix4(camera.matrixWorldInverse).z;
  giantBar.scale.setScalar(Math.min(1, Math.max(0.01, depth / GIANT_BAR_DEPTH)));
  giantBar.updateMatrixWorld();
}
/** The giant's bar on screen in CSS px (its frame's width and height), or null when any corner is off the screen or
 *  the bar is behind the near plane: the UI walk checks it stays a slim bar up close. */
function barOnScreen() {
  if (!giantBar.visible) return null;
  const hw = GIANT_BAR_W / 2 + 0.16, hh = 0.31;
  const ps = [[-hw, hh], [hw, hh], [-hw, -hh], [hw, -hh]].map(([x, y]) => giantBar.localToWorld(new THREE.Vector3(x, y, 0)));
  if (ps.some((p) => -p.clone().applyMatrix4(camera.matrixWorldInverse).z < camera.near)) return null;
  const s = ps.map((p) => p.project(camera));
  if (s.some((p) => Math.abs(p.x) > 1 || Math.abs(p.y) > 1)) return null;
  return { w: Math.round((s[1].x - s[0].x) / 2 * innerWidth), h: +((s[0].y - s[2].y) / 2 * innerHeight).toFixed(1) };
}
const bases: BaseView[] = [];
function buildBases() {
  for (const b of bases) scene.remove(b.g);
  bases.length = 0;
  sim.bases.forEach((b, p) => { const v = buildBase(b.x, b.y, TEAM_COLORS[p]); scene.add(v.g); bases.push(v); });
}
buildBases();

// ---- units: one instanced mesh per class
const mats = { foot: unitMaterial(false), horse: unitMaterial(true) };
type ClassMesh = { mesh: THREE.InstancedMesh; anim: THREE.InstancedBufferAttribute; body: THREE.InstancedBufferAttribute; n: number };
const meshes: ClassMesh[] = CLASSES.map((c) => {
  const geo = unitGeometry(c);
  const anim = new THREE.InstancedBufferAttribute(new Float32Array(CAP * 2), 2); anim.setUsage(THREE.DynamicDrawUsage);
  const body = new THREE.InstancedBufferAttribute(new Float32Array(CAP * 3), 3); body.setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute('aAnim', anim); geo.setAttribute('aBody', body);
  const mesh = new THREE.InstancedMesh(geo, c === 'cavalry' ? mats.horse : mats.foot, CAP);
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  mesh.setColorAt(0, new THREE.Color(1, 1, 1));
  mesh.frustumCulled = false;
  mesh.receiveShadow = true;
  scene.add(mesh);
  return { mesh, anim, body, n: 0 };
});
const teamCol = TEAM_COLORS.map((c) => new THREE.Color(c));

// ---- premium armies (2026-10-25): the 3D armies, baked at load (premium.ts); until one loads, its soldiers are
// placeholders. 2026-10-26: one set per army with models (premium.ts ARMY_ART), each loaded the first time a player
// in the match is on it (an army picked at the tier-1 gate), so a match downloads only the armies it has. The
// Samurai load at the start: heroes and the giant draw as Samurai figures (HERO_ARMY) until they have their own.
const premium: Partial<Record<ArmyId, Record<ClassId, PremiumClass>>> = {};
const loads = new Retry();
/** Load an army's 3D set once, the first time graphics are "best" and it is wanted (again after a failed download). */
function ensurePremium(army: ArmyId) {
  if (!premiumOn() || !ARMY_ART[army] || premium[army] || !loads.start(army, performance.now())) return;
  const qa = ((window as any).__premiumArmies ??= {}) as Record<string, string>;
  qa[army] = 'loading';
  qualityLoads++; qualityPerf = qualityWindow();
  loadArmy(`units/${army}`, CAP, army).then((p) => {
    premium[army] = p;
    for (const c of CLASSES) for (const m of [...p[c].army.meshes, ...p[c].hero?.meshes ?? []]) scene.add(m);
    stage.warm(); // their programs (and the heroes' shadow) now, not on the frame they first appear
    qa[army] = 'ready';
    if (army === HERO_ARMY) (window as any).__premium = 'ready';
  }).catch((e) => { console.error('premium army', army, e); loads.failed(army, performance.now()); qa[army] = 'failed: ' + String(e); if (army === HERO_ARMY) (window as any).__premium = 'failed: ' + String(e); }).finally(() => { qualityLoads--; qualityPerf = qualityWindow(); });
}
/** Every army the match has, and the heroes' (cheap: four players). */
function ensureArmies() {
  ensurePremium(HERO_ARMY);
  for (const pl of sim.players) if (pl.faction) ensurePremium(pl.faction);
}
/** While frames are not drawn (a jump catching up, the live hero's quiet minutes), what draw() would ask for, read
 *  the way draw() reads it (drawnAs): every army, the camps' guards, each hero's figure and the giant's, so they are in
 *  when it is shown (Flash round 2 F3, round 3 F3). */
function prefetch() {
  ensureArmies();
  const on = premiumOn();
  for (let i = 0; i < CAP; i++) {
    if (sim.state[i] === STATE.dead) continue;
    const { giant, owner, army } = drawnAs(i);
    if (army === 'neutral') ensurePremium(army);
    if (on) { if (giant) figure('giant'); else if (owner >= 0) figure(sim.players[owner].hero!.id); }
  }
}
ensureArmies();
// ---- hero figures (2026-10-26): each hero's own model, and the giant's, loaded the first time one takes the field;
// until it arrives the hero draws as its look's class. 2026-10-26: their own switch (graphics Best), not the Samurai
// army's: a figure draws as soon as it is in, whichever armies have loaded
const figures: Partial<Record<FigureId, PremiumFigure>> = {}, figureAsked = new Set<FigureId>();
function figure(id: string) {
  if (!(FIGURES as readonly string[]).includes(id)) return undefined;
  const f = id as FigureId;
  if (!figureAsked.has(f)) {
    figureAsked.add(f);
    qualityLoads++; qualityPerf = qualityWindow();
    loadFigure('units/heroes', f, f === 'giant' ? 1 : PLAYERS).then((p) => {
      figures[f] = p;
      for (const m of p.draw.meshes) scene.add(m);
      stage.warm();
    }).catch((e) => { console.error('hero figure', f, e); }).finally(() => { qualityLoads--; qualityPerf = qualityWindow(); });
  }
  return figures[f];
}
const TAU = Math.PI * 2;

// ---- effects: arrows and stones in flight, rings where a skill lands, a puff where a soldier falls (his disc shrinks
// away under it: STYLE.md's defeat pop)
/** arc: how high a shot rises at its middle (else a stone's 4, an arrow's quarter of its flight) */
type Fx = { t: number; life: number; x0: number; z0: number; x1: number; z1: number; kind: 0 | 1 | 2 | 3; team: number; r?: number; y?: number; arc?: number };
const fx: Fx[] = [];
/** Fallen horses still lying on the board (drawCorpses); cleared with fx on a fresh match. */
interface Corpse { at: number; x: number; z: number; y: number; face: number; army: ArmyId; look: ClassId; hero: boolean; team: number; s: number }
const corpses: Corpse[] = [];
const fxMesh = [
  new THREE.InstancedMesh(new THREE.BoxGeometry(0.06, 0.06, 0.7), new THREE.MeshBasicMaterial({ color: 0x3a2a1a }), 400),
  new THREE.InstancedMesh(new THREE.SphereGeometry(0.28, 8, 6), new THREE.MeshLambertMaterial({ color: 0x8a8a8a }), 100),
  new THREE.InstancedMesh(new THREE.RingGeometry(0.2, 0.55, 16), new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.85, side: THREE.DoubleSide }), 200),
  new THREE.InstancedMesh(puffGeometry(), new THREE.MeshLambertMaterial({ color: 0xf6f2ea, emissive: 0x302c26 }), FADES),
];
// ---- every soldier on a glossy disc in his team color with a soft shadow round it (STYLE.md "Bases and movement"),
// one instanced draw each whichever way he is drawn; discs past the living are the fallen's, shrinking away
const discs = teamDiscs(CAP + FADES), blobs = blobShadows(CAP);
scene.add(discs, blobs);
/** A new soldier drops onto his disc over DROP_T seconds from DROP_H (client only; the sim has him there already). */
const DROP_T = 0.35, DROP_H = 2.2;
fxMesh.forEach((m) => { m.frustumCulled = false; m.instanceMatrix.setUsage(THREE.DynamicDrawUsage); scene.add(m); });
// heroes: a ring in the team's color under each so yours is found in a brawl (VISUALS.md: under a second), and the
// skills' zones on the ground while they last (a duel's ring, an arrow storm's reach)
const ringMesh = (inner: number, n: number, opacity: number) => {
  const m = new THREE.InstancedMesh(new THREE.RingGeometry(inner, 1, 40), new THREE.MeshBasicMaterial({ transparent: true, opacity, side: THREE.DoubleSide, depthWrite: false }), n);
  m.frustumCulled = false; m.instanceMatrix.setUsage(THREE.DynamicDrawUsage); m.setColorAt(0, new THREE.Color()); m.renderOrder = 2; scene.add(m); return m;
};
const heroRings = ringMesh(0.72, PLAYERS, 0.9), zones = ringMesh(0.93, 16, 0.75);
/** A ring of the HUD's gold round each selected soldier's disc (ivory round the gold team's): a paler disc alone was
 *  lost on winter snow (controls walk, 2026-10-26). */
const selRings = ringMesh(0.8, CAP, 0.95);
/** Heroes stand taller than soldiers (ROSTER.md: heroes 1.6 to 2x a footman; GRAPHICS-PASS.md B: 1.5x). */
const HERO_SCALE = 1.5;

const uiRoot = document.getElementById('hud')!;
/** The camera's footprint on the table, four corners as x, z (the minimap's view box). */
const footprint = () => [[-1, 1], [1, 1], [1, -1], [-1, -1]].flatMap(([x, y]) => { ndc.set(x, y); ray.setFromCamera(ndc, camera); return ray.ray.intersectPlane(plane, hitP) ? [hitP.x, hitP.z] : [cam.x, cam.z]; });
/** A minimap tap moves the camera there; a watching camera then stays put a while before the director takes it back. */
let manualUntil = 0;
/** The minimap's ground: the world's grass, woods, sand and water as this season paints them, under the snow lying
 *  (in tenths, so the map repaints only when the snow visibly moves). */
function mapGround() {
  const rgb = (c: THREE.Color) => { const x = c.getHex(); return [(x >> 16) & 255, (x >> 8) & 255, x & 255] as const; };
  const pick = (f: (c: THREE.Color) => THREE.Color): Ground => {
    const G = PALETTE.ground, a = rgb(f(G.grassA)), b = rgb(f(G.grassB));
    return { road: rgb(f(G.sandA)), grass: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2], forest: rgb(f(G.forest)), water: rgb(f(PALETTE.water.shallow)) };
  };
  return groundColors(pick((c) => c), pick(summerOf), Math.round((skyNow?.cover ?? 0) * 10) / 10);
}
const guide = new GuideView(uiRoot);
/** Every command the player gives, from the HUD or the field, and the guide step it completes (tips.ts commandStep). */
/** Online a command reaches the sim a turn later, so the sim can't answer at once: ask it now whether it would take it,
 *  for the refusals the HUD speaks (a skill with nothing in reach, a tier or the extra slot not open yet). Flash F10. */
const wouldTake = (c: Command) => c.t === 'tier' ? sim.canTier(ME) : c.t === 'extra' ? sim.canExtra(ME)
  : c.t === 'cast' ? (c.k === 0 ? sim.canCastFirstNow(ME) : sim.canCast(ME, 1)) : c.t === 'buy' ? sim.canBuy(ME, c.item) : true;
/** Your command to the sim: online it goes to the room and reaches the sim when its turn comes back, on every device. */
const issue = (c: Command) => {
  // your army sent home falls back past the fight (sim.ts goal back): the Defend button and a tap on your own base
  if (c.t === 'goal' && c.goal.kind === 'base' && c.goal.p === ME) c = { ...c, back: true };
  return netMatch ? wouldTake(c) && net.cmd(c) : sim.command(ME, c);
};
const command = (c: Command) => {
  const ok = ME >= 0 && screens.screen === 'play' && issue(c), step = ok ? commandStep(c) : null;
  if (step) guide.action(step);
  return ok;
};
const hud = new Hud(uiRoot, { send: command, pickArmy: () => { pickOffered = true; screens.go('pick'); }, pause: () => screens.go('pause'),
  view: footprint, jump: (x, z) => { cam.place(x, z); placeCamera(); manualUntil = performance.now() + 8000; }, ground: mapGround,
  goal: (g) => controls.goal(g), target: () => controls.target() });
hud.setMatch(ME, sim);
const qa = params.get('qa') === '1' || capture;
/** A fresh match for seat `me` (-1 watching): the sim, the bases, and every view of the last match cleared. */
/** "Matches played" (src/net/played.ts): a match against bots counts once, when it is decided for you or you leave it
 *  after a minute of play. An online match is its room's to count, so its start marks it counted here. */
let playedSent = false;
/** The tick you took your corner at (a Jump in comes in mid-match): your minute of play counts from there. */
let playedFrom = 0;
/** The live hero's match (?live=1): framed, its first drawn frame has put the camera on the fight; ready, it has drawn
 *  with every file in. Both once a match, so a later load (an army tiering up, the giant waking) never makes Jump in
 *  fall back to the loop's link (Flash round 5 F1, F2). */
let liveFramed = false, liveReady = false;
function countPlayed() {
  if (!countsLocal({ counted: playedSent, me: ME, ticks: sim.ticks - playedFrom, staged: capture || autoplay || live })) return;
  playedSent = true;
  try { if (!navigator.sendBeacon?.('/api/played')) fetch('/api/played', { method: 'POST', keepalive: true }).catch(() => {}); } catch { /* never breaks the game */ }
}
/** The match time you played: all of it, unless you jumped in minutes into it (Flash round 2 F4) */
function playedSecs() { return Math.max(0, (sim.ticks - playedFrom) * DT); }
function freshMatch(me: number, seed = randomSeed(), seats?: readonly Seat[]) {
  playedSent = false; playedFrom = 0; liveFramed = liveReady = false;
  audio.reset();
  ME = me; newMatch(seed, seats); buildBases(); fx.length = 0; corpses.length = 0; struckAt.fill(-1e9); prevCd.fill(0); spawnAt.fill(-1e9);
  homeCamera(); placeCamera(); hud.setMatch(ME, sim); controls.setMatch();
}
// ---- online play (2026-11-01; client/online.ts the wire, net/lockstep.ts the turns). netMatch: this match is an online
// one, stepped by the room's turns. If the room goes away mid-match, bots take the other people's corners here and the
// match plays on offline, so a lost connection never strands a player in a frozen battle.
let netMatch = false;
/** ?qa=1: this page's hash at every HASH_EVERY tick of the online match, and the desyncs the room reported. */
const netHashes = new Map<number, string>();
let desyncs = 0;
/** Many turns behind the room (a late join or a reload replays the match so far; a hidden tab): step fast, no sound. */
let catchingUp = false;
const net: Online = new Online({
  changed: () => {
    if (netMatch && net.view.status === 'closed') {
      netMatch = false; catchingUp = false;
      // your own corner is yours again, even if the room had handed it to a bot (Flash F9)
      const mine = bots.findIndex((b) => b.p === ME); if (mine >= 0) bots.splice(mine, 1);
      for (let p = 0; p < PLAYERS; p++) if (p !== ME && !bots.some((b) => b.p === p)) bots.push(takeOver(sim, p));
      bots.sort((a, b) => a.p - b.p);
      hud.say(`${net.view.message ?? 'Lost the room.'} Bots play the other corners now.`, 8, 'warn');
    }
    screens.lobbyChanged();
  },
  start: (start: Start, you: number) => {
    netMatch = true; acc = 0; netHashes.clear();
    freshMatch(you, start.seed, start.seats);
    playedSent = true; // the room counts an online match
    screens.go('begin');
    stats?.start(true);
  },
  desync: () => (desyncs++, hud.say('This game fell out of step with the room. What you see may differ from the others.', 8, 'warn')),
});
function leaveNet() { if (net.active) net.leave(); netMatch = false; catchingUp = false; }
/** A seat changed hands mid-match. */
function seatSay(p: number, toBot: boolean) {
  if (catchingUp) return;
  const who = p === ME ? 'You' : `Player ${p + 1}`;
  hud.say(toBot ? (p === ME ? 'You lost the connection, so a bot held your corner.' : `${who} left. A bot took their corner.`) : (p === ME ? 'You are back in command.' : `${who} joined and took a bot's corner.`), 4);
}
/** Turns kept in hand against network jitter: the sim runs a little fast when it has more than this. */
const NET_BUFFER = 2;
/** The online frame: step as far as the room's turns allow. Returns the steps taken. */
function stepOnline(dt: number, t0: number) {
  const ls = net.ls!;
  net.flush();
  if (sim.winner >= 0) return 0; // decided: the sim no longer steps
  let n = 0;
  catchingUp = ls.behind(sim) > 20;
  if (catchingUp) {
    // replay toward the present inside a frame budget, so the page keeps drawing; effects of the skipped past are dropped
    while (ls.ready(sim) && sim.winner < 0 && performance.now() - t0 < 30) { stepSim(); n++; }
    fx.length = 0; corpses.length = 0; acc = 0;
    return n;
  }
  acc += dt * (ls.behind(sim) > NET_BUFFER + 2 ? 1.5 : 1);
  while (acc >= DT && ls.ready(sim) && n < 10) { stepSim(); acc -= DT; n++; }
  if (!ls.ready(sim)) acc = Math.min(acc, DT * 0.95); // waiting on the room: hold just short of the next step
  return n;
}
let cameraWas = settings.camera;
const screens = new Screens(uiRoot, {
  replay: () => { settings.tips = 'on'; saveSettings(settings); guide.replay(sim); },
  start: (mode: Mode, fresh: boolean) => { if (fresh) { freshMatch(mode === 'play' ? 0 : -1); stats?.start(ME >= 0); } else { hud.setMatch(ME, sim); controls.setMatch(); } },
  pick: (f) => { if (ME >= 0 && issue({ t: 'tier', faction: f })) { saveArmy(f); guide.action('tier'); stats?.army(f); } },
  pickHero: (id) => { if (ME >= 0 && sim.command(ME, { t: 'hero', id })) stats?.hero(id); },
  toTitle: () => { stats?.toTitle(playedSecs() / 60); countPlayed(); leaveNet(); freshMatch(-1); },
  toLobby: () => { const was = netMatch || ME >= 0; leaveNet(); if (was) freshMatch(-1); },
  net: {
    view: () => net.view, online: () => netMatch,
    quick: () => void net.quick(), create: () => net.create(), join: (code) => net.join(code),
    hero: (id) => net.hero(id), begin: () => net.begin(),
    cancel: () => { net.leave(); screens.lobbyChanged(); },
    link: () => net.link(),
  },
  settings: (s: Settings, key) => {
    if (key === 'quality') { Object.assign(s, manualQuality(s)); autoLight = false; qualityPerf = qualityWindow(); }
    saveSettings(s);
    if (key === 'sound' || key === 'music') audio.apply(s);
    // apply only the setting pressed, so a ?speed= in the address survives a change of graphics or camera (Astra)
    if (key === 'speed') speed = s.speed;
    if (key === 'camera' && ME >= 0) { cam.place(cam.x, cam.z, cam.dist * CAMERA_ZOOM[s.camera] / CAMERA_ZOOM[cameraWas]); placeCamera(); }
    if (key === 'quality') { stage.setTier(stageTier()); loads.forgetFailed(); ensureArmies(); stage.warm(); }
    if (key === 'blur') stage.dof = dofOn();
    cameraWas = s.camera;
  },
  // the HUD sits under every screen but only takes input in play (Astra F3: Tab could reach it behind Pause)
  changed: (s) => { stats?.screen(s, ME >= 0 && screens.mode === 'play'); if (s !== 'play' && s !== 'end') cam.stop(); /* a menu never drifts the camera */ audio.pause(s === 'pause'); qualityPerf = qualityWindow(); hud.show(s !== 'title' && s !== 'lobby'); hud.el.inert = s !== 'play'; },
}, () => sim, () => ME, settings, qa);
if (opening === 'title') screens.title(); else screens.playing(ME < 0 ? 'watch' : 'play');
// an invite link (?room=CODE) opens the lobby and joins; a reload mid-room rejoins its seat (the token in sessionStorage)
{
  const code = params.get('room')?.toUpperCase(), saved = Online.saved();
  if (opening === 'title' && (code || saved) && screens.go('online')) {
    if (code) net.join(code, saved?.code === code ? saved.token : undefined); else if (saved) net.join(saved.code, saved.token);
  }
}
stats?.start(ME >= 0); stats?.screen(screens.screen, ME >= 0 && screens.mode === 'play'); stats?.visit(viaOf(params, !!jump));
// the button counts once: its src leaves the address, so a reload or a later match is not another click (Astra
// 2026-11-02 F1). ?src=app stays: the head script reads it to know the installed app, and an app reload is an app open.
if (params.has('src') && params.get('src') !== 'app') {
  params.delete('src');
  const rest = params.toString();
  history.replaceState(history.state, '', location.pathname + (rest ? `?${rest}` : '') + location.hash);
}
// ---- Jump in (2026-11-01, jumpin.ts, jumpmatch.ts): the landing's match is rebuilt from its seed and run to its tick a
// slice a frame behind a cover (jumpStep), then you take the corner from its bot with the swap an online room makes
// when a person comes back (net/lockstep.ts humanTakes): its army, hero and gold are yours, the camera where the hero's was
const jumpCover = jump ? document.createElement('div') : null;
if (jump && jumpCover) {
  jumpCover.className = 'jumping';
  jumpCover.innerHTML = `<p class="jumping-h">Jumping in</p><p class="jumping-l">Catching up to the match at ${mmss(jump.tick * DT)}</p><div class="jumping-bar"><i></i></div>`;
  document.body.append(jumpCover);
  hud.el.inert = true; // nothing behind the cover takes focus (Flash F4); takeJump's playing('play') gives it back
}
/** Sim work a frame while the jump catches up (nothing else is on screen then). */
const JUMP_BUDGET_MS = 40;
/** What the jump arrived at: the tick, this page's hash there, the hero's, the corner taken. The landing check reads it. */
let jumped: { tick: number; hash: string; hero: string | null; seat: number | null } | null = null;
function jumpStep(t0: number) {
  const j = jump!;
  let n = 0;
  while (sim.ticks < j.tick && sim.winner < 0 && performance.now() - t0 < JUMP_BUDGET_MS) { stepSim(); n++; }
  fx.length = 0; corpses.length = 0; acc = 0;
  jumpCover?.querySelector<HTMLElement>('.jumping-bar i')?.style.setProperty('width', `${Math.round((100 * sim.ticks) / Math.max(1, j.tick))}%`);
  if (sim.ticks >= j.tick || sim.winner >= 0) takeJump(j);
  return n;
}
function takeJump(j: NonNullable<typeof jump>) {
  jump = null;
  jumped = { tick: sim.ticks, hash: sim.hash(), hero: j.hash, seat: null };
  document.documentElement.dataset.jump = j.hash === null ? 'rebuilt' : jumped.hash === j.hash ? 'same' : 'differs';
  // the address goes back to plain /play/: a reload starts fresh, and the next match has its own armies and sky (the
  // link's src went when the visit counted it)
  for (const k of JUMP_KEYS) params.delete(k);
  const rest = params.toString();
  history.replaceState(history.state, '', location.pathname + (rest ? `?${rest}` : '') + location.hash);
  skyPinned = skyPin(params);
  jumpCover?.remove();
  const p = pickSeat(sim, j.seat);
  // decided while it caught up: a match of your own instead
  if (p === null) { freshMatch(0); stats?.start(true); screens.playing('play', true); return; }
  jumped.seat = p;
  humanTakes(bots, p);
  ME = p; playedFrom = sim.ticks;
  heroOffered = !!sim.players[p].hero; pickOffered = sim.players[p].tier > 0;
  hud.setMatch(ME, sim); controls.setMatch();
  cam.stop();
  if (j.cam) cam.place(j.cam[0], j.cam[1], j.cam[2]); else homeCamera(); // no camera in the link: your base (Flash F5)
  placeCamera();
  qualityPerf = qualityWindow(); // the catch-up's slow frames are no measure of play (Flash F3)
  stats?.start(true);
  screens.playing('play');
  hud.say(`You have ${TEAM_NAME[p]}'s corner now. Its army, hero and gold are yours.`, 6);
}
addEventListener('pagehide', () => { stats?.leave(playedSecs() / 60, 'closed'); countPlayed(); });
// leaving the tab pauses a match you play (the bots would carry on without you)
document.addEventListener('visibilitychange', () => { if (document.hidden) { stats?.leave(playedSecs() / 60, 'hidden'); countPlayed(); } else stats?.back(); qualityPerf = qualityWindow(); cam.clearKeys(); if (document.hidden && ME >= 0 && !capture && !netMatch && screens.screen === 'play') screens.go('pause'); });

// ---- input (control/: gestures, selection, groups, the order marker; docs/CONTROLS.md): a tap sends your army (or
// what you selected), a drag pans, a pinch or the wheel zooms, hold and drag to select; on a desktop left drag selects
// and a right or middle drag pans
const ray = new THREE.Raycaster(), ndc = new THREE.Vector2(), plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), hitP = new THREE.Vector3(), onScreen = new THREE.Vector3();
const el = renderer.domElement;
const marks = new OrderMarker();
/** The ground point under a screen point (CSS px), or null off the table's plane. */
const toGround = (sx: number, sy: number) => { ndc.set((sx / innerWidth) * 2 - 1, -(sy / innerHeight) * 2 + 1); ray.setFromCamera(ndc, camera); return ray.ray.intersectPlane(plane, hitP) ? { x: hitP.x, z: hitP.z } : null; };
/** The camera moves only in play and on the end card: a menu (pause, the army or hero pick) never drifts it. */
const camLive = () => screens.screen === 'play' || screens.screen === 'end';
/** A drag that moved by dx, dy to x, y: the ground that was under the finger comes back under it (1:1 at any depth
 *  on screen; off the table's plane, which this camera's tilt never shows, the old screen-size rule). */
function follow(dx: number, dy: number, x: number, y: number) {
  const a = toGround(x - dx, y - dy), b = toGround(x, y), k = cam.dist / innerHeight * 1.1;
  if (a && b) cam.drag(a.x - b.x, a.z - b.z, performance.now()); else cam.drag(-dx * k, -dy * k * 1.3, performance.now());
  placeCamera();
}
scene.add(marks.root);
const controls: Controls = new Controls({
  learned: a => a === 'group' ? guide.acted('group') : guide.action(a),
  guiding: () => settings.tips === 'on' && guide.guiding,
  sim: () => sim, me: () => ME,
  at: (i) => [prevX[i] + (sim.x[i] - prevX[i]) * drawAlpha, prevY[i] + (sim.y[i] - prevY[i]) * drawAlpha],
  live: () => ME >= 0 && screens.screen === 'play' && !sim.players[ME].out && sim.winner < 0,
  send: command,
  toScreen: (x, z) => { onScreen.set(x, liftAt(bridges, x, z), z).project(camera); return onScreen.z > 1 ? null : { x: (onScreen.x + 1) / 2 * innerWidth, y: (1 - onScreen.y) / 2 * innerHeight }; },
  toGround,
  pan: (dx, dy, x, y) => {
    if (!camLive()) return;
    manualUntil = performance.now() + 8000; // a watcher's own pan holds against the director too (Astra F4)
    follow(dx, dy, x, y);
  },
  // a pinch zooms about the ground between the fingers, then pans with their midpoint
  zoom: (k, x, y, dx, dy) => {
    if (!camLive()) return;
    manualUntil = performance.now() + 8000;
    cam.zoomBy(k, toGround(x - dx, y - dy)); placeCamera(); follow(dx, dy, x, y);
  },
  jump: (x, z) => { cam.place(x, z + 6); placeCamera(); manualUntil = performance.now() + 8000; },
  say: (t, secs, tone) => hud.say(t, secs, tone),
  armyGoal: (goal) => { if (issue({ t: 'goal', goal })) { hud.flashGoal(goal); audio.play(`${sim.players[ME].faction ?? 'knights'}/shout`,0.5,true); } },
  mark: (x, z, kind) => marks.show(x, z, liftAt(bridges, x, z), kind),
  panel: (t, body, note) => hud.panel(t, body, note), closePanel: () => hud.closeSheet(),
}, hud.groups);
const pointer = (e: PointerEvent) => [e.pointerId, e.clientX, e.clientY, performance.now()] as const;
const mods = (e: PointerEvent | MouseEvent) => ({ shift: e.shiftKey, ctrl: e.ctrlKey || e.metaKey, alt: e.altKey });
el.addEventListener('pointerdown', (e) => { el.setPointerCapture(e.pointerId); cam.grab(); controls.gestures.down(...pointer(e), e.pointerType === 'mouse', e.button, mods(e)); });
el.addEventListener('pointermove', (e) => controls.gestures.move(...pointer(e)));
el.addEventListener('pointerup', (e) => { controls.gestures.up(...pointer(e)); if (!controls.gestures.busy) cam.release(performance.now()); });
el.addEventListener('pointercancel', (e) => { controls.gestures.cancel(e.pointerId); cam.grab(); }); // a taken pointer never glides
el.addEventListener('contextmenu', (e) => e.preventDefault()); // a right click is an order, not the browser's menu
// your hero's portrait picks your hero
uiRoot.addEventListener('click', (e) => { if ((e.target as Element).closest?.('.hb-face')) controls.selectHero(); });
// the wheel and a trackpad pinch set a zoom goal the camera eases to, toward the point under the cursor (camera.ts)
el.addEventListener('wheel', (e) => {
  e.preventDefault();
  if (!camLive()) return;
  cam.wheel(e.deltaY, e.deltaMode, e.ctrlKey, toGround(e.clientX, e.clientY));
  manualUntil = performance.now() + 8000; // a watcher's own zoom holds against the director, as a pan does
}, { passive: false });
// Safari pinches a trackpad with gesture events (scale since the pinch began), not ctrl wheel: the same eased zoom.
// preventDefault keeps the page from zooming. On iOS a two-finger touch sends them too, but its pointers are down
// (gestures.busy) and the pointer pinch already zooms, so those only stop the page zoom.
const safariPinch = (e: Event) => {
  e.preventDefault();
  const g = e as Event & { scale: number; clientX: number; clientY: number };
  if (!camLive() || controls.gestures.busy) return;
  if (e.type === 'gesturestart') cam.gestureStart();
  else if (e.type === 'gesturechange') cam.gesture(g.scale, isFinite(g.clientX) && isFinite(g.clientY) ? toGround(g.clientX, g.clientY) : null);
  manualUntil = performance.now() + 8000;
};
for (const t of ['gesturestart', 'gesturechange', 'gestureend']) el.addEventListener(t, safariPinch, { passive: false });
// held keys pan every frame (camera.ts step), in play and on the end card; never in a menu or while typing
const typing = (t: EventTarget | null) => t instanceof HTMLElement && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
addEventListener('keydown', (e) => {
  // typing or composing (an IME) lets go of the camera; Cmd does too, since macOS drops the keyups it hides
  if (typing(e.target) || e.isComposing || e.key === 'Meta') { cam.clearKeys(); return; }
  if (screens.screen === 'title' || screens.screen === 'lobby') return;
  const k = e.key.toLowerCase(), pans = camLive();
  if (pans && !e.ctrlKey && !e.metaKey && !e.altKey && cam.keyDown(e.code, k)) { manualUntil = performance.now() + 8000; e.preventDefault(); }
  if ((k === 'q' || k === 'e') && screens.screen === 'play') hud.key(k === 'q' ? 0 : 1);
});
// macOS sends no keyup for a key let go while Cmd is down, so letting go of Cmd lets go of every key
addEventListener('keyup', (e) => { if (e.key === 'Meta') cam.clearKeys(); else cam.keyUp(e.code); });
addEventListener('blur', () => cam.clearKeys());

// ---- the loop
const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), v3 = new THREE.Vector3(), sc = new THREE.Vector3(), up = new THREE.Vector3(0, 1, 0);
const still = new THREE.Quaternion(), flat = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI / 2), tmpCol = new THREE.Color();
const bc = [0, 1, 2, 3, 4].map(() => new THREE.Color());
/** What a selected soldier's disc is lit toward: the HUD's ivory, so every team color reads as "picked". */
const SELECT_TINT = new THREE.Color(0xfffaf0);
/** The selection ring: gold (ivory on the gold team), paler while the box is still being drawn. */
const SELECT_RING = [new THREE.Color(0xf2c443), new THREE.Color(0xfffaf0)], SELECT_PREVIEW = [new THREE.Color(0xf7dc8a), new THREE.Color(0xfffaf0)];
/** Soldiers drawn bigger than their footprint, the way toy armies read on a table (the sim radius stays 0.45). */
const UNIT_SCALE = 1.7;
/** ?qa=1: where a frame's time goes (fps.ts reads it) */
const prof = { t0: 0 as number | undefined, cpu: 0, render: 0, n: 0, tris: 0, calls: 0, sim: 0 };
let acc = 0, last = performance.now(), frames = 0, fpsT = performance.now(), fps = 0;
const director = { x: SIZE / 2, z: SIZE / 2, heat: 0 };
/** The director's camera eases with this time constant (s), and its sense of a fight's heat fades with DIRECTOR_HEAT. */
const DIRECTOR_EASE = 1.66, DIRECTOR_HEAT = 0.547;
/** The live hero's one camera height. 70 until 2026-11-02; aimed beside the logo the fight had a third of the lid, and
 *  its soldiers read as specks (tinwhistlegames 1c176083: "tiny units at the edges"), so it comes in closer. */
const LIVE_DIST = 52;
/** Watching: the camera goes to the biggest fight, not the mean of every death (2026-10-25: with four fights at once
 *  the mean was empty ground, worst zoomed in). Once a second, soldiers attacking are counted in 16 m cells; the
 *  camera moves to the busiest cell when it beats the fight it is on by half again, so it doesn't flicker. */
function aimDirector() {
  const C = 16, n = Math.ceil(SIZE / C), cnt = new Float32Array(n * n), sx = new Float32Array(n * n), sz = new Float32Array(n * n);
  let here = 0;
  for (let i = 0; i < CAP; i++) {
    if (sim.state[i] !== STATE.attack) continue;
    const x = sim.x[i], z = sim.y[i], k = Math.min(n - 1, (z / C) | 0) * n + Math.min(n - 1, (x / C) | 0);
    cnt[k]++; sx[k] += x; sz[k] += z;
    if (Math.hypot(x - director.x, z - director.z) < 12) here++;
  }
  let best = 0;
  for (let k = 1; k < cnt.length; k++) if (cnt[k] > cnt[best]) best = k;
  if (cnt[best] > here * 1.5 + 2) { director.x = sx[best] / cnt[best]; director.z = sz[best] / cnt[best]; }
}

function musicPlayer() {
  const seat = sim.players[ME >= 0 ? ME : 0];
  return seat.out ? sim.players.find(p => !p.out) ?? seat : seat;
}
const audioPoint = new THREE.Vector3();
function hear(cue: Cue) {
  let gain = cue.gain ?? 1;
  if (cue.x !== undefined && cue.y !== undefined) {
    audioPoint.set(cue.x,0,cue.y).project(camera);
    gain *= distanceGain(Math.hypot(cue.x-cam.x,cue.y-cam.z),cam.dist,Math.abs(audioPoint.x)<=1 && Math.abs(audioPoint.y)<=1 && audioPoint.z<=1);
  }
  audio.play(cue.kind,gain,cue.important,cue.delay,cue.after,cue.cueKey);
}
function stepSim() {
  prevX.set(sim.x); prevY.set(sim.y); turns.prev.set(turns.yaw); // (after a win too, or a turn would replay every tick)
  // a finished sim no longer steps or clears its events: reading them again would replay the win (Astra F2)
  if (sim.winner >= 0) return;
  if (!netMatch && myArmy && ME >= 0 && sim.players[ME].tier === 0 && sim.canTier(ME)) { /* the HUD asks; ?army= tiers automatically */ if (params.get('army')) sim.command(ME, { t: 'tier', faction: myArmy }); }
  // online: this tick's turn from the room (seat changes, everyone's commands), then the bots; offline: the bots
  if (netMatch && net.ls) { for (const e of net.ls.apply(sim, bots, (p, c) => { if (p === ME && !catchingUp) hud.refused(c); })) seatSay(e.p, e.to === 'bot'); }
  else for (const b of bots) b.tick(DT);
  const earnedBefore = ME >= 0 ? sim.players[ME].earned : 0;
  sim.step();
  // also while catching up: a slow phone can stay behind for minutes, and the room ignores hashes from long ago itself
  if (netMatch && sim.ticks % HASH_EVERY === 0) { const h = sim.hash(); net.hash(sim.ticks, h); if (qa) netHashes.set(sim.ticks, h); }
  const audible = !catchingUp && !live && !jump && screens.screen !== 'title' && screens.screen !== 'lobby' && screens.screen !== 'end';
  if (audible && ME >= 0 && sim.players[ME].earned > earnedBefore) {
    const death = sim.events.filter(e => e.e === 'death').filter(e => e.team !== ME)
      .sort((a,b) => Math.hypot(a.x-cam.x,a.y-cam.z)-Math.hypot(b.x-cam.x,b.y-cam.z))[0];
    if (death) hear({kind:'shared/coin',x:death.x,y:death.y,gain:0.35});
  }
  if (sim.ticks % 20 === 0) aimDirector();
  const audioStrikes = new Set<number>();
  for (let i = 0; i < CAP; i++) { if (sim.cd[i] > prevCd[i]) { struckAt[i] = sim.time; audioStrikes.add(i); } prevCd[i] = sim.cd[i]; }
  for (const e of sim.events) if (e.e === 'spawn' || e.e === 'hero') { struckAt[e.i] = -1e9; spawnAt[e.i] = sim.time; } // a spawn's opening cooldown is no strike (Astra)
  // every soldier turns a tick toward the sim's facing (turn.ts); draw() goes between the last two by alpha
  if (smoothTurns) for (let i = 0; i < CAP; i++) if (sim.state[i] !== STATE.dead) turns.step(i, sim.uid[i], sim.face[i], DT, turnSpec(sim.cls[i], i === sim.boss));
  if (audible) {
    const shooters = new Set(sim.events.filter(e => e.e === 'shot').map(e => e.from));
    const spawned = new Set(sim.events.filter(e => e.e === 'spawn' || e.e === 'hero').map(e => e.i));
    for (let i = 0; i < CAP; i++) {
      const cls = CLASSES[sim.cls[i]], army = sim.players[sim.team[i]]?.faction ?? 'knights';
      if (!spawned.has(i) && !shooters.has(i) && audioStrikes.has(i) && sim.state[i] === STATE.attack && !['ranged','siege','support'].includes(cls))
        hear({kind: `${army}/${swingKind(cls)}`,x:sim.x[i],y:sim.y[i],gain:0.3});
      // Sparse footsteps and support pulses from live state, no new sim events.
      if (sim.ticks % 40 === i % 40 && sim.state[i] === STATE.walk)
        hear({kind: `${army}/${cls === 'cavalry' ? 'cavalry' : 'step'}`,x:sim.x[i],y:sim.y[i],gain:0.08});
      if (cls === 'support' && sim.state[i] !== STATE.dead && sim.ticks % 160 === i % 160)
        hear({kind: `${army}/call`,x:sim.x[i],y:sim.y[i],gain:0.12});
    }
    if (ME >= 0 && sim.ticks % 100 === 0) {
      const h = sim.players[ME].hero;
      if (h && sim.state[h.i] !== STATE.dead && Math.hypot(sim.x[h.i]-sim.center.x,sim.y[h.i]-sim.center.y) < HERO_RULES.centerRadius && h.mana < HERO_RULES.mana + HERO_RULES.manaPer * (h.level - 1))
        audio.play('shared/center_mana',0.18);
    }
  }
  for (const e of sim.events) {
    if (audible) {
      if (e.e === 'tier' && e.tier === 1 && e.faction && (e.p === ME || ME < 0 && e.p === 0)) audio.pick(e.faction);
      for (const cue of eventCues(e,sim,ME)) {
        // The first pick's call covers the music switch; later tiers play tierup then call.
        if (e.e === 'tier' && e.tier === 1 && (e.p === ME || ME < 0 && e.p === 0)) continue;
        hear(cue);
      }
    }
    if (e.e === 'shot') { // a thrown stone arcs high, an arrow a little, the Great Bow's bolt flies straight
      const siege = CLASSES[e.cls] === 'siege', lob = siege && classOf('siege', sim.faction(e.team)).kind === 'blast';
      fx.push({ t: 0, life: lob ? 0.9 : 0.35, x0: e.x0, z0: e.y0, x1: e.x1, z1: e.y1, kind: lob ? 1 : 0, team: e.team, arc: siege && !lob ? 0 : undefined });
    }
    else if (e.e === 'death') { fell(e.i, e.cls, e.team, e.x, e.y); fx.push({ t: 0, life: 0.5, x0: e.x, z0: e.y, x1: e.x, z1: e.y, kind: 3, team: e.team, r: UNIT_SCALE * DISC_SCALE[CLASSES[e.cls]] * (CLASSES[e.cls] === 'hero' ? HERO_SCALE : 1), y: liftAt(bridges, e.x, e.y) }); director.heat++; }
    else if (e.e === 'cast') fx.push({ t: 0, life: 0.55, x0: e.x, z0: e.y, x1: e.x, z1: e.y, kind: 2, team: e.p, r: e.r || 1.4, y: liftUnder(bridges, e.x, e.y, e.r || 1.4) });
    else if (e.e === 'slam') fx.push({ t: 0, life: 0.5, x0: e.x, z0: e.y, x1: e.x, z1: e.y, kind: 2, team: NEUTRAL, r: e.r, y: liftUnder(bridges, e.x, e.y, e.r) });
    else if (e.e === 'bossDown') { fx.push({ t: 0, life: 0.9, x0: e.x, z0: e.y, x1: e.x, z1: e.y, kind: 3, team: NEUTRAL, r: 6, y: GIANT_Y }); director.heat += 6; }
    if (!jump && !live) hud.onEvent(e); // its lines would say a caught-up past, or over a frame nobody reads
    // your match is decided: you won, or your base fell (stats.end counts the first only)
    if (stats && ME >= 0 && (e.e === 'win' || (e.e === 'out' && e.p === ME))) {
      const me = sim.players[ME];
      stats.end(sim.winner === ME, playedSecs(), me.faction, me.hero?.id ?? null, me.tier);
    }
    if (ME >= 0 && (e.e === 'win' || (e.e === 'out' && e.p === ME))) countPlayed();
    // an online match is decided: nothing more comes from the room, so let it go (it closes once everyone has; Flash F4).
    // netMatch stays on, so the end card still offers Play online again.
    if (e.e === 'win' && netMatch && net.active) net.leave();
    // the live hero's match ends into the next, never onto an end card (Flash F2); one decided while a jump catches up is
    // thrown away for a fresh one (takeJump), so only its falls reach Screens (Flash round 2 F2)
    if (!live && !(jump && e.e === 'win')) screens.onEvent(e);
  }
  // your hero is picked as the match starts (the clock waits on the card)
  if (!heroOffered && !catchingUp && ME >= 0 && !autoplay && !capture && screens.screen === 'play' && !sim.players[ME].hero) { heroOffered = true; screens.go('hero'); }
  // the army pick opens by itself the first time you can tier up (the tier button opens it again after Not yet)
  if ((!guide.guiding || settings.tips === 'off') && !pickOffered && !catchingUp && ME >= 0 && !autoplay && !myArmy && !capture && screens.screen === 'play' && sim.players[ME].tier === 0 && sim.canTier(ME)) {
    pickOffered = true; screens.go('pick');
  }
}

/** How unit i draws: a hero as its look's class a size up, the giant as itself, everyone else as his class in his
 *  player's army (the camps' and the center's soldiers as the neutral guards, 2026-10-26, armies2). */
function drawnAs(i: number) {
  const giant = i === sim.boss, owner = sim.heroOf[i], look: ClassId = owner >= 0 ? HERO[sim.players[owner].hero!.id].look : CLASSES[sim.cls[i]];
  const big = giant ? GIANT_SCALE : owner >= 0 ? HERO_SCALE : 1, t = sim.team[i];
  const army = armyFor(giant || owner >= 0, t < PLAYERS ? sim.players[t].faction : t === NEUTRAL ? 'neutral' : null);
  return { giant, owner, look, big, t, army };
}

/** Fallen riders (2026-10-27, horses round): a unit whose 3D model has a death (the horses) plays it where it fell,
 *  lies a beat, then sinks into the board; everyone else goes at once, under the death puff. Kept apart from the sim,
 *  whose slot is free for the next spawn. */
const CORPSES = 48;
function fell(i: number, cls: number, team: number, x: number, z: number) {
  if (sim.cls[i] !== cls || sim.team[i] !== team) return; // the slot went to a new spawn before we saw the death
  const { giant, owner, look, big, t, army } = drawnAs(i);
  const pc = !giant && army ? premium[army]?.[look] : undefined;
  if (!pc?.baked.moves.death || corpses.length >= CORPSES) return;
  const base = UNIT_SCALE * (1 + sim.rank[i] * 0.04) * big;
  corpses.push({ at: sim.time, x, z, y: liftAt(bridges, x, z) + DISC_TOP * base, face: drawnFace(i), army: army!, look, hero: owner >= 0, team: t, s: base * pc.scale });
}
function drawCorpses(now: number, on: boolean) {
  for (let k = corpses.length - 1; k >= 0; k--) {
    const c = corpses[k], pc = premium[c.army]?.[c.look], death = pc?.baked.moves.death;
    const pose = pc && death && on ? corpseAt(now - c.at, death.duration, pc.baked.height * c.s) : null;
    if (!pose) { corpses.splice(k, 1); continue; }
    const a = c.hero ? pc!.hero ?? pc!.army : pc!.army;
    if (a.n >= a.cap) continue; // a full batch draws its living first
    q.setFromAxisAngle(up, Math.PI / 2 - c.face);
    a.set(a.n++, m4.compose(v3.set(c.x, c.y - pose.drop, c.z), q, sc.set(c.s, c.s, c.s)), a.rowAt('death', pose.t), teamCol[c.team]);
  }
}

function draw(alpha: number, time: number, dt: number) {
  prof.t0 = performance.now();
  drawAlpha = alpha;
  if (cam.step(dt)) placeCamera(); // held keys, the wheel's zoom and a drag's glide, before anything reads the camera
  controls.update(prof.t0); // the selection first: the discs below light from it
  for (const cm of meshes) cm.n = 0;
  ensureArmies();
  const sets = Object.values(premium), on = premiumOn();
  for (const set of sets) for (const c of CLASSES) { set![c].army.n = 0; if (set![c].hero) set![c].hero!.n = 0; }
  for (const f of Object.values(figures)) f.draw.n = 0;
  let giantFigure = false;
  const simNow = sim.time - (1 - alpha) * DT;
  for (let t = 0; t < bc.length; t++) bc[t].set(t < PLAYERS ? ARMY_BODY[sim.players[t].faction ?? 'none'] : 0x9a9aa4);
  let nd = 0, nb = 0, ns = 0;
  for (let i = 0; i < CAP; i++) {
    const st = sim.state[i];
    if (st === STATE.dead) continue;
    const x = prevX[i] + (sim.x[i] - prevX[i]) * alpha, z = prevY[i] + (sim.y[i] - prevY[i]) * alpha;
    // a hero draws as its own figure (figures.ts), or as its look's class (Musashi the armored samurai, Tomoe the
    // rider, anyone whose figure is still loading), a size up, in 3D whatever its army; the giant as its own figure (the
    // armored soldier and a drum until it loads), GIANT_SCALE up, on the tower
    const { giant, owner, look, big, t, army } = drawnAs(i);
    const face = drawnFace(i);
    q.setFromAxisAngle(up, Math.PI / 2 - face);
    if (army === 'neutral') ensurePremium(army);
    const pc = on && army ? premium[army]?.[look] ?? null : null;
    const fig = on ? (giant ? figure('giant') : owner >= 0 ? figure(sim.players[owner].hero!.id) : undefined) : undefined;
    // his disc and its shadow: the disc grows in as he comes out, he drops onto it; on a bridge, both ride its hump
    const base = UNIT_SCALE * (1 + sim.rank[i] * 0.04) * big, age = simNow - spawnAt[i], lift = liftAt(bridges, x, z);
    const drop = age < DROP_T ? DROP_H * (1 - age / DROP_T) ** 2 : 0, grow = Math.min(1, Math.max(0, age) / 0.12), dr = base * DISC_SCALE[look] * grow;
    if (!giant) { // the giant stands on the tower's stone, no disc
      // a selected soldier's disc lights up and swells a little (one in the box being drawn, a little less)
      const lit = controls.lit[i], dl = lit ? dr * (lit === 1 ? 1.12 + 0.03 * Math.sin(time * 6) : 1.06) : dr;
      if (lit) {
        const rr = dl * DISC_R * 1.22;
        selRings.setMatrixAt(ns, m4.compose(v3.set(x, lift + DISC_TOP * base + 0.02, z), flat, sc.setScalar(rr)));
        selRings.setColorAt(ns++, (lit === 1 ? SELECT_RING : SELECT_PREVIEW)[t === 2 ? 1 : 0]);
      }
      discs.setMatrixAt(nd, m4.compose(v3.set(x, lift, z), still, sc.set(dl, base, dl)));
      discs.setColorAt(nd++, lit ? tmpCol.copy(teamCol[t]).lerp(SELECT_TINT, 0.25) : t === ME ? tmpCol.copy(teamCol[t]).multiplyScalar(1.18) : teamCol[t]);
      const br = dr * DISC_R * 2 * 1.5 * (1 - 0.4 * drop / DROP_H);
      blobs.setMatrixAt(nb++, m4.compose(v3.set(x - SUN_DIR.x * 0.1 * base, lift + 0.025, z - SUN_DIR.z * 0.1 * base), still, sc.set(br, 1, br)));
    }
    const y = giant ? GIANT_Y + drop : lift + DISC_TOP * base + drop;
    if (pc || fig) {
      const own = (fig ?? pc)!, a = fig ? fig.draw : owner >= 0 ? pc!.hero ?? pc!.army : pc!.army, mv = own.baked.moves;
      if (giant && fig) giantFigure = true;
      // walk follows the sim's stride phase (one gait cycle per 2 pi, as the placeholders' legs); an attack plays from
      // the soldier's last strike and then stands until the next
      const s = base * own.scale;
      // a horse's gait by its ground speed, each at its own stride, the run only as he closes on a foe with his charge
      // ready (sim.charge alone is a stored bonus, set from his spawn; Astra) (mount.ts, 2026-10-27)
      const row = st === STATE.attack && mv.attack ? a.rowAt('attack', simNow - struckAt[i], false)
        : st === STATE.walk && mv.trot ? gaitRow(a, sim.phase[i], s, Math.hypot(sim.x[i] - prevX[i], sim.y[i] - prevY[i]) / DT / sim.speed[i], sim.charge[i] > 0 && sim.target[i] >= 0)
        : st === STATE.walk ? a.rowAt(sim.charge[i] && mv.run ? 'run' : 'walk', (sim.phase[i] / TAU) * (mv.walk?.duration ?? 1))
        : a.rowAt('idle', time + i * 0.37);
      a.set(a.n++, m4.compose(v3.set(x, y, z), q, sc.set(s, s, s)), row, teamCol[t]);
      continue;
    }
    const cm = meshes[C[look]], k = cm.n++;
    cm.mesh.setMatrixAt(k, m4.compose(v3.set(x, y, z), q, sc.set(base, base, base)));
    cm.mesh.setColorAt(k, teamCol[sim.team[i]]);
    const a = cm.anim.array as Float32Array, b = cm.body.array as Float32Array, c = bc[sim.team[i]];
    a[k * 2] = st === 2 ? time * 3.2 + i : sim.phase[i]; a[k * 2 + 1] = st;
    b[k * 3] = c.r; b[k * 3 + 1] = c.g; b[k * 3 + 2] = c.b;
  }
  drawCorpses(simNow, on);
  for (const set of sets) for (const c of CLASSES) { set![c].army.commit(); set![c].hero?.commit(); }
  for (const f of Object.values(figures)) f.draw.commit();
  for (const cm of meshes) {
    cm.mesh.count = cm.n;
    for (const a of [cm.mesh.instanceMatrix, cm.mesh.instanceColor, cm.anim, cm.body]) if (a) uploadFirst(a, cm.n);
  }
  // the giant's drum, health bar and the tower's roof
  const gi = sim.boss, standing = gi >= 0;
  giantBar.visible = standing; towerRoof.visible = !standing;
  drum.visible = standing && !giantFigure; // the giant's own figure carries its drum
  if (standing) {
    const f = drawnFace(gi), share = Math.max(0, sim.hp[gi] / sim.maxHp[gi]);
    drum.position.set(sim.x[gi] + Math.cos(f) * 1.5, GIANT_Y + GIANT_H * 0.42, sim.y[gi] + Math.sin(f) * 1.5);
    drum.rotation.set(Math.sin(time * 6) * 0.03, -f, Math.PI / 2, 'YXZ'); // its axis along the way it faces, a beat's wobble
    giantBarFill.scale.x = Math.max(0.001, share);
  }
  // hero rings (just outside the hero's disc) and skill zones
  let nr = 0, nz = 0;
  sim.players.forEach((pl, p) => {
    const i = pl.hero?.i ?? -1;
    if (i < 0) return;
    const x = prevX[i] + (sim.x[i] - prevX[i]) * alpha, z = prevY[i] + (sim.y[i] - prevY[i]) * alpha;
    const disc = UNIT_SCALE * (1 + sim.rank[i] * 0.04) * HERO_SCALE * DISC_SCALE[HERO[pl.hero!.id].look] * DISC_R, r = (disc + (p === ME ? 0.3 : 0.15)) / 0.72;
    heroRings.setMatrixAt(nr, m4.compose(v3.set(x, liftAt(bridges, x, z) + 0.06, z), flat, sc.set(r, r, r))); heroRings.setColorAt(nr++, teamCol[p]);
  });
  for (const d of sim.duels) { zones.setMatrixAt(nz, m4.compose(v3.set(d.x, liftUnder(bridges, d.x, d.y, HERO_RULES.duelRing) + 0.07, d.y), flat, sc.setScalar(HERO_RULES.duelRing))); zones.setColorAt(nz++, teamCol[sim.team[d.a]]); }
  for (const z of sim.zones) if (nz < 16) { zones.setMatrixAt(nz, m4.compose(v3.set(z.x, liftUnder(bridges, z.x, z.y, z.r) + 0.07, z.y), flat, sc.setScalar(z.r))); zones.setColorAt(nz++, teamCol[z.p]); }
  for (const [m, n] of [[heroRings, nr], [zones, nz], [selRings, ns]] as const) { m.count = n; uploadFirst(m.instanceMatrix, n); if (m.instanceColor) uploadFirst(m.instanceColor, n); }
  // effects
  const counts = [0, 0, 0, 0];
  for (let k = fx.length - 1; k >= 0; k--) { const f = fx[k]; f.t += dt; if (f.t >= f.life) fx.splice(k, 1); }
  for (const f of fx) {
    const u = f.t / f.life, mesh = fxMesh[f.kind], n = counts[f.kind]++;
    if (n >= mesh.instanceMatrix.count) continue;
    if (f.kind === 2) { mesh.setMatrixAt(n, m4.compose(v3.set(f.x0, (f.y ?? 0) + 0.1 + u * 0.4, f.z0), flat, sc.setScalar(f.r ? (f.r / 0.55) * (0.35 + 0.65 * u) : 0.6 + u * 1.6))); continue; }
    if (f.kind === 3) { // the defeat pop: a puff swells and goes, the disc under it shrinks away
      const r = f.r ?? UNIT_SCALE, y = f.y ?? 0, p = r * (0.35 + 0.65 * Math.sin(Math.min(1, u * 2.2) * Math.PI / 2)) * (1 - u ** 4);
      mesh.setMatrixAt(n, m4.compose(v3.set(f.x0, y + 0.35 * r + u * 0.6 * r, f.z0), q.setFromAxisAngle(up, u * 2), sc.setScalar(p)));
      const d = r * (1 - u) ** 1.5;
      if (nd < discs.instanceMatrix.count) { discs.setMatrixAt(nd, m4.compose(v3.set(f.x0, y, f.z0), still, sc.set(d, UNIT_SCALE * (1 - u), d))); discs.setColorAt(nd++, teamCol[f.team]); }
      continue;
    }
    const x = f.x0 + (f.x1 - f.x0) * u, z = f.z0 + (f.z1 - f.z0) * u, d = Math.hypot(f.x1 - f.x0, f.z1 - f.z0);
    const y = 1.1 + Math.sin(u * Math.PI) * (f.arc ?? (f.kind === 1 ? 4 : 0.25 * d));
    q.setFromAxisAngle(up, Math.atan2(f.x1 - f.x0, f.z1 - f.z0));
    mesh.setMatrixAt(n, m4.compose(v3.set(x, y, z), q, sc.set(1, 1, 1)));
  }
  fxMesh.forEach((m, k) => { m.count = Math.min(counts[k], m.instanceMatrix.count); uploadFirst(m.instanceMatrix, m.count); });
  (fxMesh[2].material as THREE.MeshBasicMaterial).opacity = 0.75;
  discs.count = nd; blobs.count = nb;
  for (const a of [discs.instanceMatrix, discs.instanceColor!]) uploadFirst(a, nd);
  uploadFirst(blobs.instanceMatrix, nb);
  // bases: floors by tier, the health bar facing the camera, a waving flag
  bases.forEach((bv, p) => {
    const pl = sim.players[p];
    bv.g.visible = !pl.out;
    bv.floors.forEach((f, k) => (f.visible = k < pl.tier));
    const top = 2.4 + pl.tier * 1.4;
    const roof = bv.g.getObjectByName('roof')!; roof.position.y = top + 1.1;
    bv.flag.position.set(0.9, top + 2.9, 0); bv.flag.rotation.y = Math.sin(time * 2 + p) * 0.4;
    const r = Math.max(0, pl.baseHp / pl.baseMax);
    bv.barBg.position.set(0, top + 4.2, 0); bv.bar.position.set(-3 * (1 - r), top + 4.2, 0.01); bv.bar.scale.x = Math.max(0.001, r);
    bv.barBg.quaternion.copy(camera.quaternion); bv.bar.quaternion.copy(camera.quaternion);
  });
  marks.update(dt);
  if ((ME < 0 || autoplay || sim.players[ME].out) && (camLive() || screens.screen === 'title' || screens.screen === 'lobby') && params.get('director') !== '0' && performance.now() > manualUntil) { // watching: drift to the biggest fight (aimDirector)
    // eased by time, not by frame (was 1% a frame and heat x0.97 a frame, which is these time constants at 60 fps)
    // the live hero's camera is calm (Sheep Tag, Devin 2026-10-03: cutting about on a landing disorients): three times
    // slower to follow and one height, no closing in on a hot fight
    // aimed (the live hero beside the landing's logo), the fight goes to the aim instead of a little above the middle
    const z = camZoom(), k = ease(Math.min(dt, 0.25), live ? DIRECTOR_EASE * 3 : DIRECTOR_EASE), [ax, az] = liveAim ? aimShift() : [0, -6 * z];
    cam.x += (director.x - ax - cam.x) * k; cam.z += (director.z - az - cam.z) * k; cam.dist += ((live ? LIVE_DIST : director.heat > 3 ? 55 : 80) * z - cam.dist) * k;
    director.heat *= Math.exp(-Math.min(dt, 0.25) / DIRECTOR_HEAT); placeCamera();
  }
  // the sky at this moment of the match (sky.ts), painted into the palette, the lights and the weather
  skyNow = sky.at(simNow, settings.weather === 'on', skyNow);
  stage.light(paint(skyNow));
  const musicSeat = musicPlayer();
  // an open pick plays the main theme of the army or hero it has on; leaving it hands back to the title or the match
  const menuMusic = screens.screen === 'title' || screens.screen === 'lobby', choosing = choosingArmy(screens.screen, screens.choice.army, screens.choice.hero);
  // the live hero is silent and fetches no sound at all
  if (!live) audio.update(choosing ?? (menuMusic ? lastArmy : musicSeat.faction),
    musicPhase(menuMusic || choosing !== undefined,musicSeat.tier,sim.time),
    ambienceMix(skyNow.hour,skyNow.wx.rain,skyNow.wx.snow,skyNow.wx.wind,settings.weather === 'on'), sim.time);
  if (!menuMusic && !live) { audio.warm('knights'); for (const pl of sim.players) if (pl.faction) audio.warm(pl.faction); }
  world.pools.visible = skyNow.lamps > 0.01;
  precip.update(skyNow, camera, focus.set(cam.x, 0, cam.z), stage.tier, renderer.getDrawingBufferSize(bufSize).y);
  if (sim.boss >= 0) placeGiantBar(sim.boss); // after the camera's last move this frame, so its size matches what is drawn (Astra)
  const r0 = performance.now();
  stage.follow(cam.dist);
  stage.render(time);
  if (qa) { const d = renderer.info.render; prof.cpu += r0 - (prof.t0 ?? r0); prof.render += performance.now() - r0; prof.n++; prof.tris = d.triangles; prof.calls = d.calls; }
  hud.update();
  guide.frame(sim, ME, settings.tips === 'on', screens.screen === 'play' && screens.mode === 'play' && !capture && !autoplay, dt, (x, z) => {
    onScreen.set(x, liftAt(bridges, x, z), z).applyMatrix4(camera.matrixWorldInverse);
    const behind = onScreen.z >= 0;
    if (behind) onScreen.z = -Math.max(.001, onScreen.z);
    onScreen.applyMatrix4(camera.projectionMatrix);
    const scale = behind ? 10000 / Math.max(Math.abs(onScreen.x), Math.abs(onScreen.y), .001) : 1;
    return { x: (onScreen.x * scale + 1) / 2 * innerWidth, y: (1 - onScreen.y * scale) / 2 * innerHeight };
  }, controls.count, (i) => controls.v.at(i));
}

if (qa) (window as any).__speed = (v: number) => { speed = v; };
// scripts/online-e2e.ts: the online match as this page sees it, and a command through the real path (the room's queue)
if (qa) (window as any).__online = {
  state: () => ({ status: net.view.status, message: net.view.message, code: net.view.code, you: net.view.you, seats: net.view.seats, netMatch, catchingUp, ticks: sim.ticks,
    latest: net.ls?.latest ?? -1, winner: sim.winner, desyncs, hashes: Object.fromEntries(netHashes), screen: screens.screen }),
  send: (c: Command) => command(c),
}; // scripts/fps.ts: fast-forward to the battle, then measure at 1x
// scripts/uishots.ts: walk your hero to the center, so the shop shots never wait on a slow software-GL march
if (qa) (window as any).__sim = () => sim; // scripts/uishots.ts sets up HUD states (a skill on cooldown) on the live sim
if (qa) (window as any).__face = (i: number) => [sim.face[i], drawnFace(i)]; // scripts/turnfilm.ts: the sim's facing and the drawn one
// scripts/capture.ts --skies: pin the season, weather and hour as the address would (sky.ts skyPin), on the live match
if (qa) (window as any).__sky = (q: string) => { skyPinned = skyPin(new URLSearchParams(q)); sky = new Sky(sky.seed, skyPinned); paintSeason(sky.season); return sky.season; };
if (qa) (window as any).__heroToCenter = () => { const i = sim.players[0]?.hero?.i ?? -1; if (i >= 0) { sim.x[i] = sim.center.x + 6; sim.y[i] = sim.center.y; } return i; };
// the camera, for scripts/capture.ts's shots and the UI walk's close look at the giant
if (qa || capture) (window as any).__cam = (x: number, z: number, d: number) => { cam.stop(); cam.place(x, z, d); placeCamera(); };
// scripts/camfeel.ts reads the camera's exact state each frame
if (qa || capture) (window as any).__camRig = cam;
// scripts/uishots.ts's control walk: where your soldiers are on screen, and the selection's state (control/controls.ts)
if (qa) (window as any).__controls = { toScreen: (x: number, z: number) => controls.v.toScreen(x, z), onScreen: () => controls.onScreen(), settle: () => { prevX.set(sim.x); prevY.set(sim.y); }, qa: () => ({ ...controls.qa(), marks: marks.showing, dist: Math.round(cam.dist) }) };
// the music elements now (scripts/pick-music.ts samples them through a crossfade)
if (qa) (window as any).__music = () => audio.musicState();
// the giant's bar measured now, from the camera as it is (no wait for a frame or a publish: Astra, uiart re-check)
if (qa) (window as any).__giantBar = () => { if (sim.boss < 0) return null; placeGiantBar(sim.boss); return barOnScreen(); };
function publish() {
  if (!qa) return;
  const me = ME >= 0 ? sim.players[ME] : null;
  (window as any).__qa = { time: +sim.time.toFixed(1), live: sim.live(), fps, look: stage.tier, winner: sim.winner, gold: me ? Math.floor(me.gold) : null, tier: me?.tier ?? null,
    players: sim.players.map((p) => ({ tier: p.tier, faction: p.faction, alive: p.alive, base: Math.round(p.baseHp), out: p.out, kills: p.kills,
      hero: p.hero ? { id: p.hero.id, level: p.hero.level, alive: p.hero.i >= 0, mana: Math.round(p.hero.mana), whet: p.hero.whet } : null })),
    prof: prof.n ? { sim: +(prof.sim / prof.n).toFixed(1), draw: +(prof.cpu / prof.n).toFixed(1), render: +(prof.render / prof.n).toFixed(1), tris: prof.tris, calls: prof.calls } : null,
    boss: { up: sim.boss >= 0, hp: sim.boss >= 0 ? Math.round(sim.hp[sim.boss]) : 0, wave: sim.bossWave, next: sim.nextBossAt() },
    ctl: { ...controls.qa(), marks: marks.showing },
    minimap: hud.minimap.draws, mapGround: hud.minimap.colors, figures: Object.keys(figures), cam: [Math.round(cam.x), Math.round(cam.z)], view: footprint().map(Math.round), ui: (window as any).__qa?.ui, ...(window as any).__qa?.extra,
    sky: skyNow && { season: skyNow.season, opening: skyNow.opening, hour: +skyNow.hour.toFixed(2), cloud: +skyNow.wx.cloud.toFixed(2), rain: +skyNow.wx.rain.toFixed(2), snow: +skyNow.wx.snow.toFixed(2),
      wet: +skyNow.wx.wet.toFixed(2), cover: +skyNow.cover.toFixed(2), lamps: +skyNow.lamps.toFixed(2), drops: precip.rain.visible ? precip.rain.geometry.drawRange.count / 2 : 0, flakes: precip.flakes.visible ? precip.flakes.geometry.drawRange.count : 0 } };
  prof.sim = prof.cpu = prof.render = prof.n = 0;
}

/** The title's attract match opens at this match time, when the armies have met. */
const ATTRACT_FROM = 200;
/** The live hero: held by the landing, and the frames it has drawn (the landing judges its frame rate from them). */
let livePaused = false, liveFrames = 0;
/** Starts the frame loop again after a hold (held, it schedules nothing: Flash F6). */
let liveWake: (() => void) | null = null;
// what the landing reads for Jump in and drives (src/site/livehero.ts LiveApi); same origin, so no messages
if (live) (window as any).__live = {
  // the corner in view is the one around the fight it films, at the aim when there is one
  state: () => { const [ax, az] = aimShift(); return { ready: liveReady, frames: liveFrames, seed: matchSeed, tick: sim.ticks, seat: seatInView(sim, cam.x + ax, cam.z + az),
    hash: sim.hash(), cam: [cam.x + ax, cam.z + az, cam.dist], armies: armiesOf((params.get('armies') ?? '').split(',')) }; },
  pause: (on: boolean) => { const was = livePaused; livePaused = on; if (was && !on) liveWake?.(); },
  aim: (a: Aim | null) => { liveAim = a; },
  fighters: () => {
    const out: Aim[] = [];
    camera.updateMatrixWorld();
    for (let i = 0; i < CAP; i++) {
      if (sim.state[i] !== STATE.attack) continue;
      aimRay.set(sim.x[i], 0.8, sim.y[i]).project(camera);
      if (Math.abs(aimRay.x) <= 1 && Math.abs(aimRay.y) <= 1 && aimRay.z < 1) out.push([+((aimRay.x + 1) / 2).toFixed(3), +((1 - aimRay.y) / 2).toFixed(3)]);
    }
    return out;
  },
};
// Jump in's arrival (the landing check holds it to the hero's hash)
(window as any).__jumped = () => jumped;
stage.warm(); // every program the first frames want, before them (the 3D armies warm again when they arrive)
if (capture) {
  // video capture: the page draws only when asked, so every frame is a fixed slice of game time
  // alpha: draw that far between the last two ticks (scripts/turnfilm.ts films three frames a tick, 60 fps of game time)
  (window as any).__advance = (ticks: number, frameDt = 1 / 20, alpha = 1) => {
    for (let k = 0; k < ticks && sim.winner < 0; k++) stepSim();
    draw(alpha, sim.time, frameDt * Math.max(1, ticks / 2)); publish();
    return { time: sim.time, winner: sim.winner };
  };
  draw(1, 0, 0); publish();
} else {
  /** The one frame queued, or 0: a wake never queues a second loop beside one still pending (a hidden tab holds its
   *  frame; Flash re-check F1). Held, the loop queues nothing; liveWake starts it again. */
  let raf = 0;
  const next = () => { raf = requestAnimationFrame(frame); };
  const frame = (now: number) => {
    raf = 0;
    const dt = Math.min(0.25, (now - last) / 1000); last = now;
    // the live hero held by the landing (scrolled away, or another 3D scene has the turn): no sim, no drawing
    if (livePaused) return;
    const runs = clockRuns(screens.screen), online = netMatch && net.ls !== null && screens.screen !== 'title' && screens.screen !== 'lobby';
    // stopped (paused, picking), the clock holds where it was: alpha 0 would draw everyone a tick back (Astra, 2026-10-30)
    if (!online) { if (runs) acc += dt * speed; } // online, stepOnline keeps its own time by the room's turns
    let n = 0;
    const s0 = performance.now();
    if (screens.screen === 'title' || screens.screen === 'lobby' || live) {
      // the attract match (and the live hero's): skip its quiet first minutes a few hundred ticks a frame, and start another when one ends
      // a new attract match; behind the lobby that must not leave the room being joined (Flash F1)
      if (sim.winner >= 0) { if (screens.screen === 'lobby' || live) freshMatch(-1); else screens.hooks.toTitle(); }
      // inside a frame budget too: 300 steps a frame on a slow phone starved the page (an online lobby's socket waited
      // seconds for its turn), 2026-11-01
      let skipped = 0;
      for (; skipped < 300 && sim.time < ATTRACT_FROM && performance.now() - s0 < 16; skipped++) stepSim();
      // its last batch too: the effects of seconds nobody saw would all go off on the first frame drawn (Flash round 3 F4)
      if (skipped) { fx.length = 0; corpses.length = 0; acc = 0; }
    }
    // catching up, the cover is all there is: no drawing behind it, and no quality judge of frames spent on the sim (Flash F3)
    // the frame it arrives on too: the frame-rate count and its clock start with play (Flash round 2 F1)
    // and draws once it has arrived: the cover never lifts off a canvas nothing has drawn (Flash round 3 F2)
    if (jump) { jumpStep(s0); prefetch(); if (!jump) draw(1, now / 1000, 0); frames = 0; fpsT = last = performance.now(); next(); return; }
    if (online) n = stepOnline(dt, s0);
    else while (acc >= DT && n < 60 && clockRuns(screens.screen)) { stepSim(); acc -= DT; n++; }
    if (qa) prof.sim += performance.now() - s0;
    if (n === 60 && !online) acc = 0;
    frames++;
    if (now - fpsT > 500) {
      fps = Math.round((frames * 1000) / (now - fpsT)); frames = 0; fpsT = now; publish();
      stats?.fps(fps, screens.screen === 'play' && !document.hidden && speed > 0 && speed <= 2);
    }
    const quality = autoQuality(qualityPerf, {
      dt, tier: stage.tier, enabled: settings.autoQuality && !qa && !live && !params.has('tier'),
      playing: screens.screen === 'play' && speed > 0 && speed <= 2, hidden: document.hidden, loading: qualityLoads > 0,
    });
    qualityPerf = quality.state;
    if (quality.tier !== stage.tier) {
      if (quality.tier === 'light') autoLight = true;
      stage.setTier(quality.tier); stage.warm();
      hud.say(`${quality.tier === 'light' ? 'Lighter effects' : 'Best effects'} for smoother play. Adjust in Settings.`, 5);
    }
    // the live hero's next match runs through its quiet minutes undrawn: the canvas holds the last frame (Flash F2)
    // and, until the match is ready, while its files load: the next match's new armies never show as stand-ins over a
    // hero already on screen (Flash round 6 F1)
    if (live && (sim.time < ATTRACT_FROM || (!liveReady && qualityLoads > 0))) { fx.length = 0; corpses.length = 0; prefetch(); next(); return; } // nothing saved up to go off at once (Flash re-check F2)
    // its first frame drawn opens on the fight at the live height: the director only eases inside draw(), so it would
    // still be gliding in from the overview over the frames judged and shown (Flash round 5 F2)
    if (live && !liveFramed) {
      liveFramed = true; const z = camZoom(); cam.place(director.x, director.z + 6 * z, LIVE_DIST * z); placeCamera();
      if (liveAim) { const [ax, az] = aimShift(); cam.place(director.x - ax, director.z - az, LIVE_DIST * z); placeCamera(); }
      director.heat = 0;
    }
    draw(Math.min(1, acc / DT), now / 1000, dt);
    if (live && !liveReady && qualityLoads === 0) liveReady = true;
    liveFrames++;
    next();
  };
  liveWake = () => { if (raf) return; last = performance.now(); next(); };
  next();
}
export { FACTIONS, FACTION, TIER_COST, TIER_GATE, BASE, NEUTRAL };
